import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import properties from '../data/properties.json';
import PropertyCard, { getPropertyTypeLabel, propertyPath } from './PropertyCard';
import { SITE_URL, useSeo } from '../seo';

const isVideo = (src) => src && src.endsWith('.mp4');

// "Casa en Venta, Ruta Viva | J&K Realty" -> "Casa en Venta, Ruta Viva"
const headingOf = (p) => p.seoTitle.replace(/\s*\|\s*J&K Realty$/, '');

// "$398.000" -> { value: 398000, monthly: false }; "$2.300/mes" -> { value: 2300, monthly: true }
const parsePrice = (price) => {
  if (!price) return null;
  const digits = price.replace(/\/mes.*$/i, '').replace(/[^\d]/g, '');
  if (!digits) return null;
  return { value: Number(digits), monthly: /\/mes/i.test(price) };
};

const truncate = (text, max) => {
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  return cut.slice(0, cut.lastIndexOf(' ')).replace(/[.,;:\s]+$/, '') + '…';
};

const buildDescription = (p) => {
  const named = p.title.toLowerCase().includes(p.zone.toLowerCase());
  const parts = [named ? p.title : `${p.title} en ${p.zone}`];
  if (p.snippet) parts.push(p.snippet);
  if (p.price) parts.push(p.price);
  return truncate(parts.join('. ') + '. Agenda tu visita con J&K Realty.', 155);
};

const buildJsonLd = (p, path) => {
  const photos = p.images.filter((src) => !isVideo(src)).slice(0, 6).map((src) => SITE_URL + src);
  const price = parsePrice(p.price);
  const listing = {
    '@context': 'https://schema.org',
    '@type': 'RealEstateListing',
    name: headingOf(p),
    url: SITE_URL + path,
    description: p.description.split('\n')[0],
    image: photos,
    about: { '@type': 'Place', name: p.address, address: { '@type': 'PostalAddress', addressLocality: p.zone, addressRegion: 'Pichincha', addressCountry: 'EC' } },
    // Referencia al agente completo definido en index.html, sin duplicarlo.
    provider: { '@id': `${SITE_URL}/#agent` },
  };
  if (price) {
    listing.offers = {
      '@type': 'Offer',
      price: price.value,
      priceCurrency: 'USD',
      availability: p.status === 'sold' ? 'https://schema.org/SoldOut' : 'https://schema.org/InStock',
      url: SITE_URL + path,
    };
    if (price.monthly) {
      listing.offers.priceSpecification = { '@type': 'UnitPriceSpecification', price: price.value, priceCurrency: 'USD', unitCode: 'MON' };
    }
  }
  const breadcrumbs = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Inicio', item: SITE_URL + '/' },
      { '@type': 'ListItem', position: 2, name: 'Propiedades', item: SITE_URL + '/propiedades' },
      { '@type': 'ListItem', position: 3, name: headingOf(p), item: SITE_URL + path },
    ],
  };
  return [listing, breadcrumbs];
};

const PropertyGallery = ({ property }) => {
  const [index, setIndex] = useState(0);
  const media = property.images;
  const current = media[index];

  const next = () => setIndex((i) => (i + 1) % media.length);
  const prev = () => setIndex((i) => (i - 1 + media.length) % media.length);

  return (
    <div className="property-modal-gallery">
      <div className="gallery-main">
        {isVideo(current) ? (
          <video
            key={current}
            src={current}
            className="gallery-image"
            controls
            controlsList="nodownload"
            autoPlay
            muted
            loop
            playsInline
            onContextMenu={(e) => e.preventDefault()}
          />
        ) : (
          <img
            src={current}
            alt={`${property.title} en ${property.address} - foto ${index + 1} | J&K Realty`}
            className="gallery-image"
            width="1200"
            height="900"
            decoding="async"
            {...(index === 0 ? { fetchpriority: 'high' } : {})}
          />
        )}

        {media.length > 1 && (
          <>
            <button className="gallery-nav gallery-nav-prev" onClick={prev} aria-label="Elemento anterior">‹</button>
            <button className="gallery-nav gallery-nav-next" onClick={next} aria-label="Siguiente elemento">›</button>
            <div className="gallery-indicator">{`${index + 1} / ${media.length}`}</div>
          </>
        )}
      </div>
    </div>
  );
};

const NotFound = () => (
  <main className="property-page">
    <div className="container property-not-found">
      <h1>Esta propiedad no existe</h1>
      <p>Puede que el enlace esté mal escrito o que la propiedad ya no esté disponible.</p>
      <Link to="/propiedades" className="btn btn-primary">Ver propiedades</Link>
    </div>
  </main>
);

const PropertyContent = ({ property }) => {
  const path = propertyPath(property);

  useSeo({
    title: property.seoTitle,
    description: buildDescription(property),
    path,
    image: property.ogImage,
    imageAlt: `${property.title} en ${property.address}`,
    jsonLd: buildJsonLd(property, path),
  });

  // Las tres propiedades siguientes en orden circular: así todas las fichas reciben el mismo
  // número de enlaces internos (antes las últimas de la lista no recibían ninguno desde otras fichas).
  const position = properties.findIndex((p) => p.id === property.id);
  const related = Array.from({ length: Math.min(3, properties.length - 1) }, (_, i) => properties[(position + 1 + i) % properties.length]);

  return (
    <main className="property-page">
      <div className="container">
        <nav className="property-breadcrumbs" aria-label="Migas de pan">
          <Link to="/">Inicio</Link>
          <span aria-hidden="true">›</span>
          <Link to="/propiedades">Propiedades</Link>
          <span aria-hidden="true">›</span>
          <span aria-current="page">{headingOf(property)}</span>
        </nav>

        <article className="property-page-card">
          <PropertyGallery property={property} />

          <div className="property-modal-details">
            <div className="property-modal-header">
              {property.status === 'sold' && <span className="property-badge sold">VENDIDA</span>}
              {property.featured && property.status !== 'sold' && (
                <span className="property-badge featured">DESTACADA</span>
              )}
              <h1>{headingOf(property)}</h1>
              <p className="property-modal-address">{property.address}</p>
              <div className="property-modal-price">
                {property.price ? `${property.price} • ${getPropertyTypeLabel(property.type)}` : getPropertyTypeLabel(property.type)}
              </div>
            </div>

            {property.highlights && property.highlights.length > 0 && (
              <section className="property-modal-highlights">
                <h2>Datos Destacables</h2>
                <div className="highlights-grid">
                  {property.highlights.map((highlight) => (
                    <div key={highlight.label} className="highlight-item">
                      <span className="highlight-label">{highlight.label}</span>
                      <span className="highlight-value">{highlight.value}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {property.description && (
              <section className="property-modal-description">
                <h2>Descripción</h2>
                <p>{property.description}</p>
              </section>
            )}

            <div className="property-modal-actions">
              <a
                href={`https://wa.me/593995410584?text=${encodeURIComponent(`Hola Karen, me interesa: ${property.title} (${SITE_URL}${path})`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="btn btn-primary"
              >
                Consultar por WhatsApp
              </a>
              <a href="tel:+593995410584" className="btn btn-secondary">Llamar Ahora</a>
            </div>
          </div>
        </article>

        <section className="related-properties">
          <h2 className="related-title">Otras propiedades</h2>
          <div className="properties-grid">
            {related.map((p) => (
              <PropertyCard key={p.id} property={p} />
            ))}
          </div>
          <div className="properties-cta">
            <Link to="/propiedades" className="btn btn-primary">Ver todas las propiedades</Link>
          </div>
        </section>
      </div>
    </main>
  );
};

const PropertyPage = () => {
  const { slug } = useParams();
  const property = properties.find((p) => p.slug === slug);
  return property ? <PropertyContent property={property} /> : <NotFound />;
};

export default PropertyPage;
